import { useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

import type { SkillDistributionItem } from '../../types/reports';
import { useTheme } from '../../context/ThemeContext';
import AccessibleChartSummary from './AccessibleChartSummary';

const CHART_LIMIT = 10;

export default function SkillDistributionChart({
  data,
}: {
  data: SkillDistributionItem[];
}) {
  const { theme } = useTheme();
  const [showAllUsed, setShowAllUsed] = useState(true);
  const [showUnused, setShowUnused] = useState(true);

  const usedSkills = [...data]
    .filter((x) => x.userCount > 0)
    .sort((a, b) => b.userCount - a.userCount);

  const unusedSkills = [...data]
    .filter((x) => x.userCount === 0)
    .sort((a, b) => a.skillName.localeCompare(b.skillName));

  const chartData = usedSkills.slice(0, CHART_LIMIT);
  const remainingUsedSkills = usedSkills.slice(CHART_LIMIT);

  const totalSkills = data.length;

  const topSkill =
    chartData.length > 0
      ? chartData.reduce((prev, current) =>
          prev.userCount > current.userCount ? prev : current
        )
      : null;

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div
        className="grid grid-cols-3 gap-4 rounded-lg border p-4"
        style={{
          borderColor: theme.border,
          backgroundColor: theme.surface,
        }}
      >
        <div>
          <div
            className="text-sm"
            style={{ color: theme.textSecondary }}
          >
            Total Skills
          </div>

          <div
            className="text-2xl font-bold"
            style={{ color: theme.text }}
          >
            {totalSkills}
          </div>
        </div>

        <div>
          <div
            className="text-sm"
            style={{ color: theme.textSecondary }}
          >
            Most Common Skill
          </div>

          <div
            className="text-xl font-bold"
            style={{ color: theme.text }}
          >
            {topSkill?.skillName ?? '-'}
          </div>
        </div>

        <div>
          <div
            className="text-sm"
            style={{ color: theme.textSecondary }}
          >
            Total Skill Assignments
          </div>

          <div
            className="text-2xl font-bold"
            style={{ color: theme.text }}
          >
            {usedSkills.length}
          </div>

          <div className="text-xs" style={{ color: theme.textSecondary }}>
            Across all {usedSkills.length} skills with experts (chart + list below)
          </div>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={360}>
        <BarChart
          layout="vertical"
          data={chartData}
          margin={{
            top: 10,
            right: 30,
            // left: 100,
            bottom: 10,
          }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#E5E7EB"
          />

          <XAxis
            type="number"
            allowDecimals={false}

          />

          <YAxis
            type="category"
            dataKey="skillName"
            width={140}
            tick={{ fontSize: 14 }}
            
          />

          <Tooltip
            formatter={(value) => [
              `${value} Experts`,
              'Experts',
            ]}
          />

          <Bar
            dataKey="userCount"
            fill={theme.primary}
            radius={[0, 6, 6, 0]}
          />
        </BarChart>
      </ResponsiveContainer>

      <AccessibleChartSummary
        caption="Top skills by expert count"
        categoryHeader="Skill"
        valueHeader="Experts"
        rows={chartData.map((item) => ({
          label: item.skillName,
          value: item.userCount,
        }))}
      />

      <div
        className="text-center text-sm"
        style={{ color: theme.textSecondary }}
      >
        Showing top {chartData.length} of {usedSkills.length} skills with experts assigned
        {unusedSkills.length > 0 ? ` (${totalSkills} skills total in catalog)` : ''}
      </div>
     <div className="flex flex-col gap-4 md:flex-row ">
  {remainingUsedSkills.length > 0 && (
    <div
      className="flex-1 rounded-lg border p-4"
      style={{ borderColor: theme.border }}
    >
      <button
        type="button"
        onClick={() => setShowAllUsed((prev) => !prev)}
        className="text-sm font-semibold"
        style={{ color: theme.primary }}
      >
        {showAllUsed ? 'Hide' : 'Show'} {remainingUsedSkills.length} more skill
        {remainingUsedSkills.length === 1 ? '' : 's'} not shown in the chart
      </button>

      {showAllUsed && (
            <ul
              className={`mt-3 grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2  ${
                remainingUsedSkills.length > 5 ? 'max-h-36 overflow-y-auto pr-5' : ''
              }`}
            >
          {remainingUsedSkills.map((item) => (
            <li
              key={item.skillId}
              className="flex items-center justify-between text-sm"
              style={{ color: theme.text }}
            >
              <span>{item.skillName}</span>
              <span style={{ color: theme.textSecondary }}>
                {item.userCount} experts
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )}

  {unusedSkills.length > 0 && (
    <div
      className="flex-1 rounded-lg border p-4"
      style={{ borderColor: theme.border }}
    >
      <button
        type="button"
        onClick={() => setShowUnused((prev) => !prev)}
        className="text-sm font-semibold"
        style={{ color: theme.primary }}
      >
        {showUnused ? 'Hide' : 'Show'} {unusedSkills.length} skill
        {unusedSkills.length === 1 ? '' : 's'} with no experts assigned yet
      </button>

      {showUnused && (
            <ul
              className={`mt-3 grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2 ${
                unusedSkills.length > 5 ? 'max-h-36 overflow-y-auto pr-2' : ''
              }`}
            >
          {unusedSkills.map((item) => (
            <li
              key={item.skillId}
              className="text-sm"
              style={{ color: theme.textSecondary }}
            >
              {item.skillName}
            </li>
          ))}
        </ul>
      )}
    </div>
  )}
</div>
    </div>
  );
}
